// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EmptyState } from '@/components/empty-state';
import {
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  PersonOutlineOutlined,
  SwapVertRounded,
  TimelineOutlined,
  ViewAgendaOutlined,
  WidgetsOutlined,
} from '@/constants/hardfin-standard-icons';
import { SortDirection, invert } from '@/constants/sort';
import { CostFilter } from '@/costs/api/get-costs';
import { CostTimeSeriesPoint } from '@/costs/api/types';
import { CurrencySelector } from '@/costs/components/currency-selector';
import { HorizontalBarChart } from '@/costs/components/horizontal-bar-chart';
import { VerticalBarChart } from '@/costs/components/vertical-bar-chart';
import { useCostTimeSeries } from '@/costs/hooks/use-cost-time-series';
import { useCurrencySelector } from '@/costs/hooks/use-currency-selector';
import { truncateLabel } from '@/costs/utils/date-utils';
import { extendTimeSeriesData } from '@/costs/utils/time-series-utils';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  Box,
  CardContent,
  Divider,
  FormControl,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';

interface CostAnalyticsProps {
  filter?: Omit<CostFilter, 'pageSize' | 'pageNumber' | 'sort'>;
}

type TabValue = 'person' | 'project' | 'category';
type IntervalValue = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
type SortProperty = 'name' | 'amount';

interface ChartData {
  label: string;
  fullLabel: string; // Store full label for tooltip
  value: number;
  rawData: CostTimeSeriesPoint;
}

/**
 * Analytics component showing cost breakdowns and time series data
 */
export function CostAnalytics(props: CostAnalyticsProps) {
  const [interval, setInterval] = useState<IntervalValue>('weekly');
  const [personSort, setPersonSort] = useState<{
    property: SortProperty;
    direction: SortDirection;
  }>({ property: 'amount', direction: 'desc' });
  const [projectSort, setProjectSort] = useState<{
    property: SortProperty;
    direction: SortDirection;
  }>({ property: 'amount', direction: 'desc' });
  const [categorySort, setCategorySort] = useState<{
    property: SortProperty;
    direction: SortDirection;
  }>({ property: 'amount', direction: 'desc' });
  const [chartHeights, setChartHeights] = useState<{
    horizontal?: number;
    vertical?: number;
  }>({});

  const personSortMenu = useDisclosure();
  const projectSortMenu = useDisclosure();
  const categorySortMenu = useDisclosure();
  const personSortButtonRef = useRef<HTMLButtonElement>(null);
  const projectSortButtonRef = useRef<HTMLButtonElement>(null);
  const categorySortButtonRef = useRef<HTMLButtonElement>(null);
  const horizontalChartRef = useRef<HTMLDivElement>(null);
  const verticalChartRef = useRef<HTMLDivElement>(null);

  // Fetch time series data
  const {
    data: timeData,
    isLoading: isLoadingTime,
    isFetching: isFetchingTime,
  } = useCostTimeSeries({
    filter: props.filter,
    interval: interval,
    groupBy: undefined, // Time series
  });

  // Fetch grouped data for each category
  const {
    data: personData,
    isLoading: isLoadingPerson,
    isFetching: isFetchingPerson,
  } = useCostTimeSeries({
    filter: props.filter,
    groupBy: 'person',
  });

  const {
    data: projectData,
    isLoading: isLoadingProject,
    isFetching: isFetchingProject,
  } = useCostTimeSeries({
    filter: props.filter,
    groupBy: 'project',
  });

  const {
    data: categoryData,
    isLoading: isLoadingCategory,
    isFetching: isFetchingCategory,
  } = useCostTimeSeries({
    filter: props.filter,
    groupBy: 'category',
  });

  const { availableCurrencies, activeCurrencyCode, setSelectedCurrency } =
    useCurrencySelector([timeData, personData, projectData, categoryData]);

  // Transform time series data with complete date range
  const timeChartData: ChartData[] = useMemo(() => {
    const points = (timeData?.points ?? []).filter(
      (point) => point.isoCode === activeCurrencyCode
    );
    if (activeCurrencyCode === null || points.length === 0) return [];

    const createEmptyPoint = (date: DateTime) => ({
      dates: [date],
      workers: null,
      projects: null,
      categories: null,
      isoCode: activeCurrencyCode,
      totalAmount: '0',
      totalEntries: 0,
      averageAmount: '0',
    });

    const extendedData = extendTimeSeriesData(
      points,
      { interval: (timeData?.interval ?? interval) as IntervalValue },
      createEmptyPoint
    );

    // Set the value for each data point
    return extendedData.map((point) => ({
      ...point,
      value: parseFloat(point.rawData.totalAmount ?? '0'),
    }));
  }, [timeData, interval, activeCurrencyCode]);

  // Transform grouped data for each category
  const personChartData: ChartData[] = useMemo(() => {
    if (!personData?.points) return [];
    return transformGroupedData(
      personData.points.filter((point) => point.isoCode === activeCurrencyCode),
      'person',
      personSort.property,
      personSort.direction
    );
  }, [
    personData,
    personSort.property,
    personSort.direction,
    activeCurrencyCode,
  ]);

  const projectChartData: ChartData[] = useMemo(() => {
    if (!projectData?.points) return [];
    return transformGroupedData(
      projectData.points.filter(
        (point) => point.isoCode === activeCurrencyCode
      ),
      'project',
      projectSort.property,
      projectSort.direction
    );
  }, [
    projectData,
    projectSort.property,
    projectSort.direction,
    activeCurrencyCode,
  ]);

  const categoryChartData: ChartData[] = useMemo(() => {
    if (!categoryData?.points) return [];
    return transformGroupedData(
      categoryData.points.filter(
        (point) => point.isoCode === activeCurrencyCode
      ),
      'category',
      categorySort.property,
      categorySort.direction
    );
  }, [
    categoryData,
    categorySort.property,
    categorySort.direction,
    activeCurrencyCode,
  ]);

  // Helper function to transform grouped data
  function transformGroupedData(
    points: CostTimeSeriesPoint[],
    groupType: TabValue,
    sortProperty: SortProperty,
    sortDirection: SortDirection
  ): ChartData[] {
    const transformedData = points.map((point) => {
      let fullLabel: string;

      switch (groupType) {
        case 'person':
          if (point.workers && point.workers.length > 0) {
            fullLabel = `${point.workers[0].firstName} ${point.workers[0].lastName}`;
          } else {
            fullLabel = 'Not set';
          }
          break;
        case 'project':
          if (point.projects && point.projects.length > 0) {
            fullLabel = point.projects[0].name;
          } else {
            fullLabel = 'Not set';
          }
          break;
        case 'category':
          if (point.categories && point.categories.length > 0) {
            fullLabel = point.categories[0];
          } else {
            fullLabel = 'Not set';
          }
          break;
        default:
          fullLabel = 'Not set';
      }

      const value = parseFloat(point.totalAmount ?? '0');

      // Truncate label for display, but keep full label for tooltip (16 chars for horizontal chart)
      const displayLabel = truncateLabel(fullLabel, 16);

      return {
        label: displayLabel,
        fullLabel,
        value,
        rawData: point,
      };
    });

    // Apply local sorting
    return transformedData.sort((a, b) => {
      if (sortProperty === 'name') {
        // Handle "Not set" specially - always last for ascending, first for descending
        const aIsNotSet = a.fullLabel === 'Not set';
        const bIsNotSet = b.fullLabel === 'Not set';

        if (aIsNotSet && bIsNotSet) return 0;
        if (aIsNotSet) return sortDirection === 'asc' ? 1 : -1;
        if (bIsNotSet) return sortDirection === 'asc' ? -1 : 1;

        // Normal string comparison for other values
        const comparison = a.fullLabel.localeCompare(b.fullLabel);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      // Sort by amount
      const comparison = a.value - b.value;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  // Capture chart heights after rendering to reduce flicker on subsequent loads
  useEffect(() => {
    const isCurrentlyLoading =
      isLoadingTime || isLoadingPerson || isLoadingProject || isLoadingCategory;

    if (!isCurrentlyLoading) {
      const captureHeights = () => {
        const newHeights: { horizontal?: number; vertical?: number } = {};

        if (horizontalChartRef.current) {
          const rect = horizontalChartRef.current.getBoundingClientRect();
          if (rect.height > 0) {
            newHeights.horizontal = rect.height;
          }
        }

        if (verticalChartRef.current) {
          const rect = verticalChartRef.current.getBoundingClientRect();
          if (rect.height > 0) {
            newHeights.vertical = rect.height;
          }
        }

        if (Object.keys(newHeights).length > 0) {
          setChartHeights((prev) => ({ ...prev, ...newHeights }));
        }
      };

      // Capture heights after a brief delay to ensure charts are fully rendered
      const timeout = setTimeout(captureHeights, 100);
      return () => clearTimeout(timeout);
    }
  }, [
    isLoadingTime,
    isLoadingPerson,
    isLoadingProject,
    isLoadingCategory,
    personChartData,
    projectChartData,
    categoryChartData,
    timeChartData,
  ]);

  // Computed values after all hooks
  const isLoading =
    isLoadingTime || isLoadingPerson || isLoadingProject || isLoadingCategory;

  const handleIntervalChange = (event: SelectChangeEvent<IntervalValue>) => {
    const newInterval = event.target.value as IntervalValue;
    setInterval(newInterval);
  };

  const handlePersonSortChange = (property: SortProperty) => {
    if (personSort.property === property) {
      // Toggle direction if same property
      setPersonSort((prev) => ({
        ...prev,
        direction: invert(prev.direction),
      }));
    } else {
      // New property, default to ascending for name, descending for amount
      setPersonSort({
        property,
        direction: property === 'name' ? 'asc' : 'desc',
      });
    }
  };

  const handleProjectSortChange = (property: SortProperty) => {
    if (projectSort.property === property) {
      // Toggle direction if same property
      setProjectSort((prev) => ({
        ...prev,
        direction: invert(prev.direction),
      }));
    } else {
      // New property, default to ascending for name, descending for amount
      setProjectSort({
        property,
        direction: property === 'name' ? 'asc' : 'desc',
      });
    }
  };

  const handleCategorySortChange = (property: SortProperty) => {
    if (categorySort.property === property) {
      // Toggle direction if same property
      setCategorySort((prev) => ({
        ...prev,
        direction: invert(prev.direction),
      }));
    } else {
      // New property, default to ascending for name, descending for amount
      setCategorySort({
        property,
        direction: property === 'name' ? 'asc' : 'desc',
      });
    }
  };

  // Show loading skeleton during initial load
  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ width: '100%', maxWidth: '100%' }}>
        <CardContent sx={{ paddingTop: 1 }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={400}
            sx={{ borderRadius: 1 }}
          />
        </CardContent>
      </Paper>
    );
  }

  // The charts plot money, so there is nothing to show when no priced currency
  // is present (the summary still reports any no-currency entries separately).
  if (availableCurrencies.length === 0) {
    return (
      <Paper variant="outlined" sx={{ width: '100%', maxWidth: '100%' }}>
        <CardContent sx={{ paddingTop: 1, margin: 2 }}>
          <EmptyState title="No priced data for this range" />
        </CardContent>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ width: '100%', maxWidth: '100%' }}>
      <CardContent sx={{ paddingTop: 1, margin: 2 }}>
        <Stack spacing={0}>
          {/* Currency selector — only when costs span more than one currency */}
          {availableCurrencies.length > 1 && activeCurrencyCode && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <CurrencySelector
                currencies={availableCurrencies}
                value={activeCurrencyCode}
                onChange={setSelectedCurrency}
              />
            </Box>
          )}
          {/* Time Series Chart with Vertical Bars */}
          <Box>
            {isFetchingTime && !isLoadingTime && (
              <LinearProgress sx={{ mb: 1 }} />
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <TimelineOutlined fontSize="small" />
                <Typography variant="h6">Over time</Typography>
              </Stack>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select value={interval} onChange={handleIntervalChange}>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box
              ref={verticalChartRef}
              sx={{
                position: 'relative',
                // Force fixed height during any time series loading to prevent jumps
                height:
                  isFetchingTime && !isLoadingTime
                    ? chartHeights.vertical || 400
                    : 'auto',
                minHeight: chartHeights.vertical || 400,
              }}
            >
              {isFetchingTime && !isLoadingTime ? (
                // During fetching, show skeleton and hide chart completely
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 2,
                    backgroundColor: 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    sx={{ borderRadius: 1 }}
                  />
                </Box>
              ) : null}
              <Box
                sx={{
                  visibility:
                    isFetchingTime && !isLoadingTime ? 'hidden' : 'visible',
                  height: '100%',
                }}
              >
                {isFetchingTime && !isLoadingTime ? (
                  // Render stable placeholder during fetch
                  <div style={{ width: '100%', height: '100%' }} />
                ) : (
                  <VerticalBarChart
                    key={`${interval}-${timeChartData.length}`}
                    data={timeChartData}
                    loading={isFetchingTime}
                    displayMode="currency"
                    currencyCode={activeCurrencyCode ?? undefined}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Divider between charts */}
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <Divider sx={{ width: '100%' }} />
          </Box>

          {/* Person Chart */}
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <PersonOutlineOutlined fontSize="small" />
                <Typography variant="h6">By person</Typography>
              </Stack>
              <IconButton
                ref={personSortButtonRef}
                onClick={personSortMenu.open}
              >
                <SwapVertRounded />
              </IconButton>
            </Box>
            {isFetchingPerson && !isLoadingPerson && (
              <LinearProgress sx={{ mb: 1 }} />
            )}
            <Box sx={{ mb: 3 }}>
              <HorizontalBarChart
                data={personChartData}
                loading={isFetchingPerson}
                displayMode="currency"
                currencyCode={activeCurrencyCode ?? undefined}
              />
            </Box>
          </Box>

          {/* Divider between charts */}
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <Divider sx={{ width: '100%' }} />
          </Box>

          {/* Project Chart */}
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <WidgetsOutlined fontSize="small" />
                <Typography variant="h6">By project</Typography>
              </Stack>
              <IconButton
                ref={projectSortButtonRef}
                onClick={projectSortMenu.open}
              >
                <SwapVertRounded />
              </IconButton>
            </Box>
            {isFetchingProject && !isLoadingProject && (
              <LinearProgress sx={{ mb: 1 }} />
            )}
            <Box sx={{ mb: 3 }}>
              <HorizontalBarChart
                data={projectChartData}
                loading={isFetchingProject}
                displayMode="currency"
                currencyCode={activeCurrencyCode ?? undefined}
              />
            </Box>
          </Box>

          {/* Divider between charts */}
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <Divider sx={{ width: '100%' }} />
          </Box>

          {/* Category Chart */}
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <ViewAgendaOutlined fontSize="small" />
                <Typography variant="h6">By category</Typography>
              </Stack>
              <IconButton
                ref={categorySortButtonRef}
                onClick={categorySortMenu.open}
              >
                <SwapVertRounded />
              </IconButton>
            </Box>
            {isFetchingCategory && !isLoadingCategory && (
              <LinearProgress sx={{ mb: 1 }} />
            )}
            <Box>
              <HorizontalBarChart
                data={categoryChartData}
                loading={isFetchingCategory}
                displayMode="currency"
                currencyCode={activeCurrencyCode ?? undefined}
              />
            </Box>

            {/* Person Sort Menu */}
            <Menu
              anchorEl={personSortButtonRef.current}
              open={personSortMenu.isOpen}
              onClose={personSortMenu.close}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {(['name', 'amount'] as SortProperty[]).map((property) => {
                const isActive = personSort.property === property;
                const isInset = !isActive;

                return (
                  <MenuItem
                    key={property}
                    onClick={(event) => {
                      event.stopPropagation();
                      handlePersonSortChange(property);
                    }}
                  >
                    {isActive && (
                      <ListItemIcon>
                        {personSort.direction === 'asc' ? (
                          <ArrowUpwardRounded fontSize="small" />
                        ) : (
                          <ArrowDownwardRounded fontSize="small" />
                        )}
                      </ListItemIcon>
                    )}
                    <ListItemText inset={isInset}>
                      Sort by {property}
                    </ListItemText>
                  </MenuItem>
                );
              })}
            </Menu>

            {/* Project Sort Menu */}
            <Menu
              anchorEl={projectSortButtonRef.current}
              open={projectSortMenu.isOpen}
              onClose={projectSortMenu.close}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {(['name', 'amount'] as SortProperty[]).map((property) => {
                const isActive = projectSort.property === property;
                const isInset = !isActive;

                return (
                  <MenuItem
                    key={property}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleProjectSortChange(property);
                    }}
                  >
                    {isActive && (
                      <ListItemIcon>
                        {projectSort.direction === 'asc' ? (
                          <ArrowUpwardRounded fontSize="small" />
                        ) : (
                          <ArrowDownwardRounded fontSize="small" />
                        )}
                      </ListItemIcon>
                    )}
                    <ListItemText inset={isInset}>
                      Sort by {property}
                    </ListItemText>
                  </MenuItem>
                );
              })}
            </Menu>

            {/* Category Sort Menu */}
            <Menu
              anchorEl={categorySortButtonRef.current}
              open={categorySortMenu.isOpen}
              onClose={categorySortMenu.close}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {(['name', 'amount'] as SortProperty[]).map((property) => {
                const isActive = categorySort.property === property;
                const isInset = !isActive;

                return (
                  <MenuItem
                    key={property}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleCategorySortChange(property);
                    }}
                  >
                    {isActive && (
                      <ListItemIcon>
                        {categorySort.direction === 'asc' ? (
                          <ArrowUpwardRounded fontSize="small" />
                        ) : (
                          <ArrowDownwardRounded fontSize="small" />
                        )}
                      </ListItemIcon>
                    )}
                    <ListItemText inset={isInset}>
                      Sort by {property}
                    </ListItemText>
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>
        </Stack>
      </CardContent>
    </Paper>
  );
}
